import React, { useRef, useState, useEffect, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { General, MediaDevices } from 'icons/untitledui';
import colours from 'theme/atoms/colours';

import { ReflexStep } from 'shiftNotes/_types';
import { useAddShiftNoteAttachmentMutation } from 'shared/hooks/api/shiftNotes';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';

import * as Styled from 'shiftNotes/styles/InstantReflex/ReflexStepCard.style';
import ReflexStepCard from './ReflexStepCard';

interface AskForPhotoCardProps {
  step: ReflexStep;
  stepNumber: number;
  totalSteps: number;
  noteId: string;
  reflexId: string;
  label?: string;
}

const SUCCESS_FLASH_MS = 2000;

export default function AskForPhotoCard({
  step,
  stepNumber,
  totalSteps,
  noteId,
  reflexId,
  label,
}: AskForPhotoCardProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  const shiftNotesCapture = usePosthogCapture();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutate: addAttachment, isPending } =
    useAddShiftNoteAttachmentMutation(noteId);

  useEffect(() => {
    if (!showSuccess) return undefined;
    const timer = window.setTimeout(
      () => setShowSuccess(false),
      SUCCESS_FLASH_MS
    );
    return () => window.clearTimeout(timer);
  }, [showSuccess]);

  const handleClick = () => {
    setErrorMessage(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    addAttachment(formData, {
      onSuccess: () => {
        setShowSuccess(true);
        shiftNotesCapture.noteReflexPhotoAttached({
          shift_note_id: noteId,
          reflex_id: reflexId,
          step_id: step.id,
        });
      },
      onError: () => {
        shiftNotesCapture.noteReflexPhotoError({
          shift_note_id: noteId,
          reflex_id: reflexId,
          step_id: step.id,
        });
        setErrorMessage(t('instantReflex.photoUploadError'));
      },
    });
  };

  const pillLabel = (() => {
    if (isPending) return t('instantReflex.uploadingPhoto');
    if (showSuccess) return t('instantReflex.photoUploadSuccess');
    return t('instantReflex.addPhoto');
  })();

  const pillIcon = (() => {
    if (isPending) return <General.Loading02 />;
    if (showSuccess) return <General.Check />;
    return <MediaDevices.ImagePlus />;
  })();

  return (
    <ReflexStepCard
      label={
        label ??
        (totalSteps === 1
          ? t('instantReflex.todoLabel')
          : t('instantReflex.stepLabel', { n: stepNumber }))
      }
      labelColor={colours.blue[500]}
      icon={<General.CheckCircle />}
      title={step.title}
    >
      <Styled.ActionsRow>
        <Styled.ActionPill
          type="button"
          onClick={handleClick}
          $isDisabled={isPending}
          aria-label={t('instantReflex.addPhoto')}
        >
          {pillIcon}
          {pillLabel}
        </Styled.ActionPill>
      </Styled.ActionsRow>
      {errorMessage && (
        <Styled.SourcesLabel>{errorMessage}</Styled.SourcesLabel>
      )}
      <Styled.HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
      />
    </ReflexStepCard>
  );
}
